/* eslint-disable no-nested-ternary */
// src/hooks/useWallDesigner.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useCabinetVisualizerStore, useCabinetVisualizerDigitalDataStore } from '../../../../store';
import {
  selectDesignProducts,
  selectDesignSegments,
  selectWallName,
  selectTotalHeight,
  selectTotalDepth,
  selectDesignAnchorSide,
  selectDesignById,
  selectCurrentStyleChoiceId,
  selectProductsModalType,
  DEFAULT_VIEWPORT_WALLSIZE,
  selectDesignMatrixSizeForDisplay,
  WALL_SEGMENT_HEIGHT
} from '../../../../store/cabinetVisualizer/selectors';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../utils/empty-reference';
import {
  sendRemoveAnalytics,
  sendEditCabinetAnalytics,
  sendRemoveItemFromWallAnalytics
} from '../../../../pages/cabinetVisualizer/analytics/analyticsFunctions';
import { CABINET_VISUALIZER_DIGITAL_DATA } from '../../../../constants';
import {
  zoomIncrement,
  mobileZoomOffset,
  defaultZoom,
  handleAutoZoom,
  getOccupiedWidth
} from '../WallDesigner/utils/handleAutoZoom';
import { inchesToPxs, getPxInInches } from '../../../../utils/wall-size-utils';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';
import { useDrawerStore, useCabinetStore, useViewportStore } from '../../../../store/ui';
import {
  editProduct,
  isCornerCabinetByItemId,
  isMirrorRequired,
  removeProduct
} from '../utils';
import {
  isCollidedWithOtherProducts,
  getShiftedProducts,
  getDefaultMinMaxColumns,
} from '../WallDesigner/dnd/handleDragAndDrop';
import {
  getAdjustedCabinetOriginForEdit,
  isBaseProduct
} from '../WallDesigner/utils/cabinetLayout';
import { tryGetQueryParam } from '../../../../utils/queryParams';
import { CABINET_SKU, DIRECTION } from '../../cabinetConstants';

export const useWallDesigner = (designId) => {

  const { trackChooseCabinetOverlayEvent } = useCabinetVisualizerDigitalDataStore();

  const trackChooseCabinetAnalytics = () => {
    trackChooseCabinetOverlayEvent({
      eventName: CABINET_VISUALIZER_DIGITAL_DATA.CONFIGURATOR.CHOOSE_CABINET,
    });
  };

  const {
    cabinetVisualizer, updateDesignProducts, addCabinetToDesign, updateCabinetVisualizer
  } = useCabinetVisualizerStore();

  const {
    superSkuProducts,
  } = useCabinetVisualizerStore((state) => state.cabinetVisualizer.skuDrawer);

  const dndEnabled = tryGetQueryParam('dndEnabled') === 'true';
  const enableTouchPointsOnBothSides = true; // tryGetQueryParam('enableTouchPointsOnBothSides') === 'true';
  const wallContainerRef = useRef(null);

  const editCabinetRef = useRef(null);
  const [editCabinetAnalyticsPending, setEditCabinetAnalyticsPending] = useState(false);
  const [editCabinetAnalytics, setEditCabinetAnalytics] = useState({});

  const isMobile = useIsMobileDevice();

  const DEFAULT_WIDTH = DEFAULT_VIEWPORT_WALLSIZE.Columns;
  const { ProductParentIds } = selectDesignById(cabinetVisualizer, designId) || EMPTY_OBJECT;
  const host = useConfigService('global-custom-commerce:host');
  const metadataBaseUrl = useConfigService('graphQL:metadataBaseUrl');
  const anchorSide = selectDesignAnchorSide(cabinetVisualizer, designId);
  const [cornerCabinetItemIds, setCornerCabinetItemIds] = useState([]);
  const { Rows, Columns } = selectDesignById(cabinetVisualizer, designId)?.Matrix || EMPTY_OBJECT;

  const getProductType = (cabinet) => {
    const itemId = cabinet.Sku;
    const productType = selectProductsModalType(cabinetVisualizer, itemId, designId);
    if (productType === 'Filler') {
      if (cabinet.Origin.Row > 0
        || Math.abs(cabinet.Origin.Row) + cabinet.Origin.Height > DEFAULT_VIEWPORT_WALLSIZE.Rows) {

        return 'BaseFiller';
      }
      return 'WallFiller';
    }
    return productType;
  };
  const getCornerCabinetWithVoidSpacer = (voidSpacer, cornerCabinets) => {
    const EPSILON = 0.01;
    const spacerColumn = voidSpacer.Origin.Column;
    const spacerRow = voidSpacer.Origin.Row;
    const spacerWidth = voidSpacer.Origin.Width;
    const cornerCabinet = cornerCabinets.find((cab) => {
      // get corner cabinet column if void spacer is on left
      const cornerCabinetColumnLeft = spacerColumn + spacerWidth;
      // get corner cabinet column if void spacer is on right
      const cornerCabinetColumnRight = spacerColumn - cab.Origin.Width;

      return Math.abs(cab.Origin.Row - spacerRow) < EPSILON
        && (Math.abs(cab.Origin.Column - cornerCabinetColumnLeft) < EPSILON
        || Math.abs(cab.Origin.Column - cornerCabinetColumnRight) < EPSILON);
    });
    if (cornerCabinet) {
      cornerCabinet.mirror = spacerColumn > cornerCabinet.Origin.Column;
      return cornerCabinet;
    }

    return null;
  };
  const getUpdatedCabinetsWithVoidSpacers = (
    updatedCabinets, updatedCornerCabinetItemIds) => {
    if (_isEmpty(updatedCornerCabinetItemIds) || !updatedCabinets.some((cab) => cab.Sku === CABINET_SKU.VOID_SPACE)) {
      return updatedCabinets;
    }
    const voidSpacers = updatedCabinets.filter((cab) => cab.Sku === CABINET_SKU.VOID_SPACE);
    const cornerCabinets = updatedCabinets.filter((cab) => updatedCornerCabinetItemIds.includes(cab.Sku));
    const newCabinets = [...updatedCabinets];
    voidSpacers.forEach((voidSpacer) => {
      const cornerCabinet = getCornerCabinetWithVoidSpacer(voidSpacer, cornerCabinets);
      if (cornerCabinet) {
        const idx = newCabinets.findIndex((cab) => cab.id === cornerCabinet.id && cab.Sku !== CABINET_SKU.VOID_SPACE);
        if (idx !== -1) {
          newCabinets[idx].id = voidSpacer.id;
          newCabinets[idx].mirror = cornerCabinet.mirror;
        }
      }
    });
    return newCabinets;
  };

  const updateCabinetVisualizerStore = ({
    updatedProducts,
    cabinetVisualizerStore
  }) => {
    const designs = cabinetVisualizerStore.designs;
    const currDesign = designs[designId];
    currDesign.Products = updatedProducts;
    updateCabinetVisualizer({
      ...currDesign,
    });
  };
  const [initialLoad, setInitialLoad] = useState(true);
  const getUpdatedProducts = () => {
    const cabinets = selectDesignProducts(cabinetVisualizer, designId);
    if (!cabinets || cabinets.length === 0) {
      return EMPTY_ARRAY;
    }

    let cornerCabinetHasChanged = false;
    let updatedCornerCabinetItemIds = [...cornerCabinetItemIds];
    const updatedCabinets = cabinets.map((cabinet) => {
      const productType = getProductType(cabinet);
      const cabinetId = cabinet.id || uuidv4();
      const itemId = cabinet.Sku;
      const parentId = ProductParentIds ? ProductParentIds[`${itemId}`] : null;
      const mirror = isMirrorRequired(cabinet);
      if (itemId && parentId && superSkuProducts && superSkuProducts.length > 0) {
        const {
          cornerCabinet,
          cornerCabinetType
        } = isCornerCabinetByItemId(String(parentId), superSkuProducts);

        if (cornerCabinet && !updatedCornerCabinetItemIds.includes(itemId)) {
          updatedCornerCabinetItemIds.push(itemId);
          cornerCabinetHasChanged = true;
        }
        return {
          ...cabinet,
          cornerCabinet,
          cornerCabinetType,
          productType,
          id: cabinetId,
          mirror
        };
      }
      return { ...cabinet, productType, id: cabinetId, mirror };
    });

    let finalUpdatedCabinets = getUpdatedCabinetsWithVoidSpacers(
      updatedCabinets,
      updatedCornerCabinetItemIds
    );

    if (initialLoad) {
      setInitialLoad(false);
      finalUpdatedCabinets = getShiftedProducts(finalUpdatedCabinets, anchorSide);
      updateCabinetVisualizerStore({
        updatedProducts: finalUpdatedCabinets,
        cabinetVisualizerStore: cabinetVisualizer
      });
    }
    if (cornerCabinetHasChanged) {
      setCornerCabinetItemIds(updatedCornerCabinetItemIds);
    }
    return updatedCabinets;
  };
  const [products, setProducts] = useState(getUpdatedProducts());

  const arraysAreDifferent = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return true;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < arr1.length; i++) {
      if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) return true;
    }
    return false;
  };
  useEffect(() => {
    if (ProductParentIds
      && superSkuProducts
      && superSkuProducts.length > 0) {
      const updatedCabinets = getUpdatedProducts();
      if (arraysAreDifferent(products, updatedCabinets)) {
        setProducts(updatedCabinets);
        updateCabinetVisualizerStore({
          updatedProducts: updatedCabinets,
          cabinetVisualizerStore: cabinetVisualizer
        });
      }
    }

  }, [ProductParentIds, superSkuProducts]);
  // Get the current styleChoiceId from the store
  const storeStyleChoiceId = selectCurrentStyleChoiceId(cabinetVisualizer);
  const hasSelectedCollection = !!storeStyleChoiceId;
  const segments = selectDesignSegments(cabinetVisualizer, designId);

  const wallName = selectWallName(cabinetVisualizer, designId);
  const wallSize = selectDesignMatrixSizeForDisplay(cabinetVisualizer, designId);
  const totalWidth = getOccupiedWidth(products);
  const totalHeight = Math.ceil(selectTotalHeight(cabinetVisualizer, designId));
  const totalDepth = Math.ceil(selectTotalDepth(cabinetVisualizer, designId));

  const [toggleZoomModal, setToggleZoomModal] = useState(false);
  const [segmentsEnabled, setSegmentsEnabled] = useState(false);
  const [showDimensions, setShowDimensions] = useState(false);
  const [restartModalOpen, setRestartModalOpen] = useState(false);
  const [editWallModalProps, setEditWallModalProps] = useState({ open: false });

  const restartHeader = 'Restart Wall';
  const restartMessage1 = 'Are you sure you want to restart this design? ';
  const restartMessage = `${restartMessage1}Restarting will clear all elements from this wall.`;
  const switchButtonPosition = true;
  const editCabinetPricingAvailable = editCabinetRef.current;

  const onEditCabinetAnalytics = (oldItemId, cabinetInfo) => {
    setEditCabinetAnalyticsPending(true);
    setEditCabinetAnalytics({ oldItemId, cabinetInfo });
  };

  useEffect(() => {
    // When editCabinetAnalyticsPending is set, mark the ref as pending and reset the flag
    if (editCabinetAnalyticsPending) {
      editCabinetRef.current = true;
      setEditCabinetAnalyticsPending(false);
    }
  }, [editCabinetAnalyticsPending]);

  useEffect(() => {
    // Only run analytics if the ref is marked as pending and the pending flag is false
    if (editCabinetRef.current && !editCabinetAnalyticsPending) {
      const { oldItemId, cabinetInfo } = editCabinetAnalytics;
      sendEditCabinetAnalytics(oldItemId, cabinetInfo, cabinetVisualizer.itemPrices);
      editCabinetRef.current = false; // Reset ref after sending analytics
    }
  }, [editCabinetAnalyticsPending, editCabinetAnalytics, cabinetVisualizer.itemPrices]);

  const handleEditWallModalOpen = () => setEditWallModalProps({ open: true });
  const handleEditWallModalClose = () => setEditWallModalProps({ open: false });
  // Safety check: ensure we have a valid design with required data
  const currentDesign = cabinetVisualizer.designs?.[designId];
  const hasValidDesign = currentDesign && currentDesign.Matrix;

  const activeCabinet = useCabinetStore((state) => state.activeCabinet);
  const disableTouchPoints = activeCabinet !== null;
  const viewportInfo = useViewportStore((state) => state.viewportInfo);
  const setViewportInfo = useViewportStore((state) => state.setViewportInfo);

  const selectedCabinet = useCabinetStore((state) => state.selectedCabinet);
  const setSelectedCabinet = useCabinetStore((state) => state.setSelectedCabinet);

  const pxInInches = getPxInInches(viewportInfo?.width || DEFAULT_WIDTH, DEFAULT_WIDTH);
  const zoomedPxInInches = (viewportInfo?.finalZoom || defaultZoom) * pxInInches;
  const [currentFloorHeight, setCurrentFloorHeight] = useState(0);
  const zoom = viewportInfo?.finalZoom || defaultZoom;
  const floorHeightInInches = (isMobile ? 24 : 12) / zoom; // Default floor height in inches
  const floorHeight = inchesToPxs(floorHeightInInches, zoomedPxInInches);

  const [floorRect, setFloorRect] = useState(null);

  const restartButtonHeight = 42;
  const restartButtonWidth = 155;
  const iconButtonHeight = 32;
  const iconButtonWidth = 32;
  const iconButtonGap = 8;
  const [buttonPosition, setButtonPosition] = useState(null);
  const [iconPosition, setIconPosition] = useState(null);

  const updateButtonAndIconPositions = () => {

    if (floorRect && wallContainerRef.current && buttonPosition === null && iconPosition === null) {
      const wallRect = wallContainerRef.current.getBoundingClientRect();
      // Calculate the center of the floor relative to the wall container
      const floorCenterY = floorRect.top + floorRect.height / 2;
      const wallBottom = wallRect.bottom;
      const bottomOffset = wallBottom - floorCenterY;
      const offset = iconButtonHeight / 4;
      const offsetForButton = restartButtonHeight / 4;
      setButtonPosition({
        bottom: (wallBottom - floorRect.top) / 2 - restartButtonHeight + offsetForButton, // center the button
      });
      setIconPosition({
        bottom: isMobile ? 5 : (wallBottom - floorRect.top) / 2 - iconButtonHeight + offset, // center the icon
      });
    }
  };

  const handleWallContainerRefChange = useCallback((node) => {
    wallContainerRef.current = node;
    updateButtonAndIconPositions();
  }, []);

  useEffect(() => {
    if (currentFloorHeight !== floorHeight) {
      setCurrentFloorHeight(floorHeight);

      updateButtonAndIconPositions();
    }
  }, [floorHeight]);

  const [confirmation, setConfirmation] = useState(false);
  const handleClose = () => {
    if (confirmation) setConfirmation(false);
  };

  const openConfirmation = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setConfirmation(true);
    sendRemoveItemFromWallAnalytics('delete cabinet button');
  };

  const removeCabinet = useCallback(() => (event) => {
    event.preventDefault();
    event.stopPropagation();

    setSelectedCabinet(null);
    let updatedProducts = EMPTY_ARRAY;
    const itemId = selectedCabinet?.Sku;
    const parentId = ProductParentIds ? ProductParentIds[`${itemId}`] : '';

    const hasProduct = products.some((product) => product.Sku !== -3);
    const filteredProducts = products.filter((product) => product.Sku !== -3);
    if (hasProduct && filteredProducts.length > 1) {
      const toDeleteCabinetInfo = {
        parentId,
        itemId,
        origin,
        id: selectedCabinet?.id
      };
      updatedProducts = removeProduct(
        cabinetVisualizer, designId, itemId, parentId, origin,
        toDeleteCabinetInfo, products, dndEnabled, enableTouchPointsOnBothSides);

      updatedProducts = getShiftedProducts(updatedProducts, anchorSide);
    }
    setProducts(updatedProducts);
    updateDesignProducts(
      host,
      designId,
      updatedProducts,
      { parentId, itemId }, metadataBaseUrl);

    setConfirmation(!confirmation);
    const itemPrice = cabinetVisualizer.itemPrices.find((item) => item.itemId === itemId);
    sendRemoveAnalytics([{
      quantity: 1,
      productInfo: {
        sku: String(itemId)
      },
      price: {
        basePrice: String(itemPrice?.price)
      }
    }]);
    handleClose();
  });

  const getRemoveAllAnalyticsData = () => {
    const items = [];
    products.forEach((product) => {
      const itemPrice = cabinetVisualizer.itemPrices.find((item) => item.itemId === product.Sku);
      const updateExisting = items.findIndex((item) => item.productInfo.sku === String(product.Sku));
      if (updateExisting !== -1) {
        items[updateExisting].quantity += 1;
        // Update basePrice to be unit price * new quantity
        const unitPrice = Number(itemPrice?.price) || 0;
        items[updateExisting].price.basePrice = String(unitPrice * items[updateExisting].quantity);
      } else {
        const unitPrice = Number(itemPrice?.price) || 0;
        items.push({
          quantity: 1,
          productInfo: {
            sku: String(product.Sku)
          },
          price: {
            basePrice: String(unitPrice)
          }
        });
      }
    });
    return items;
  };

  const removeCabinets = async () => {
    const target = 'restart button';
    sendRemoveAnalytics(getRemoveAllAnalyticsData(), target);
    const updatedProducts = EMPTY_ARRAY;
    const addedProductInfo = EMPTY_OBJECT;

    setProducts(updatedProducts);
    await updateDesignProducts(
      host,
      designId,
      updatedProducts,
      addedProductInfo,
      metadataBaseUrl
    );
    setViewportInfo({
      finalZoom: defaultZoom,
      initialZoom: defaultZoom,
    });
  };

  const handleCloseRestartModal = () => setRestartModalOpen(false);

  const handleConfirmRestart = async () => {
    await removeCabinets();
    handleCloseRestartModal();
  };

  useEffect(() => {
    if (!products || products === EMPTY_ARRAY) {
      setRestartModalOpen(false);
    }
  }, [products]);

  const handleDimensionsToggle = () => {
    setShowDimensions(!showDimensions);
  };

  const handleZoomIn = () => {
    const newZoom = viewportInfo.finalZoom + zoomIncrement;

    setViewportInfo({
      ...viewportInfo,
      finalZoom: newZoom
    });

  };

  const handleZoomOut = () => {
    const newZoom = viewportInfo.finalZoom - zoomIncrement;
    setViewportInfo({
      ...viewportInfo,
      finalZoom: newZoom
    });
  };

  const handleToggleSegments = () => {
    setSegmentsEnabled(!segmentsEnabled);
  };

  const handleZoom = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setToggleZoomModal(!toggleZoomModal);
  };

  const drawerOpen = useDrawerStore((state) => state.drawerOpen);
  const setDrawerOpen = useDrawerStore((state) => state.setDrawerOpen);
  const collectionDrawerOpen = useDrawerStore((state) => state.collectionDrawerOpen);
  const setCollectionDrawerOpen = useDrawerStore((state) => state.setCollectionDrawerOpen);
  // Use global state for spacerModalOpen
  const spacerModalOpen = useCabinetVisualizerStore((state) => state.cabinetVisualizer.skuDrawer.spacerModalOpen);
  const setSpacerModalOpen = (open) => updateCabinetVisualizer({ skuDrawer: { spacerModalOpen: open } });
  const [pendingSpacerModal, setPendingSpacerModal] = useState(false);
  const selectedTouchPoint = useDrawerStore((state) => state.selectedTouchPoint);
  const setSelectedTouchPoint = useDrawerStore((state) => state.setSelectedTouchPoint);
  const [editMode, setEditMode] = useState(false);
  const toggleDrawer = useCallback((event, newTouchPoint) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // If event is null, it means CabinetCollectionDrawer is closing after collection selection
    // In this case, we want to keep the drawer open so CabinetDrawer can take over
    if (event === null) {
      // Don't toggle the drawer state, let it remain open for CabinetDrawer
      return;
    }

    if (selectedCabinet) {
      setSelectedCabinet(null);
    }

    if (newTouchPoint) {
      setSelectedTouchPoint(newTouchPoint);
    }

    if (hasSelectedCollection) {
      if (drawerOpen === false) {
        trackChooseCabinetAnalytics();
      }
      setDrawerOpen(true);
    } else {
      if (collectionDrawerOpen === false) {
        trackChooseCabinetAnalytics();
      }
      setCollectionDrawerOpen(true);
    }
  });

  const toggleFillerDrawer = useCallback((event, newTouchPoint) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // If event is null, it means CabinetCollectionDrawer is closing after collection selection
    // In this case, we want to keep the drawer open so CabinetDrawer can take over
    if (event === null) {
      // Don't toggle the drawer state, let it remain open for CabinetDrawer
      return;
    }

    setSelectedCabinet(null);
    let updatedTouchPoint = null;

    if (newTouchPoint) {
      const fillerType = newTouchPoint.type[0].toLowerCase() === 'wall' ? ['WallFiller'] : ['BaseFiller'];
      updatedTouchPoint = {
        ...newTouchPoint,
        type: fillerType,
      };
    }

    if (hasSelectedCollection) {
      if (drawerOpen === false) {
        trackChooseCabinetAnalytics();
      }
      setDrawerOpen(true);
    } else {
      if (collectionDrawerOpen === false) {
        trackChooseCabinetAnalytics();
      }
      setCollectionDrawerOpen(true);
    }

    if (updatedTouchPoint) {
      setSelectedTouchPoint(updatedTouchPoint);
    }
  }, [drawerOpen, setDrawerOpen, setSelectedTouchPoint]);

  const toggleSpacerModal = useCallback((event, newTouchPoint) => {
    event.preventDefault();
    event.stopPropagation();

    setSelectedCabinet(null);
    if (newTouchPoint) {
      setSelectedTouchPoint(newTouchPoint);
    }
    if (hasSelectedCollection) {
      setSpacerModalOpen(!spacerModalOpen);
      trackChooseCabinetAnalytics();
    } else if (!spacerModalOpen) {
      setPendingSpacerModal(true);
      setCollectionDrawerOpen(true);
    }
  }, [spacerModalOpen, setSelectedTouchPoint, hasSelectedCollection,
    setCollectionDrawerOpen, spacerModalOpen, setPendingSpacerModal]);

  const handleRenderCallback = useCallback((width) => handleAutoZoom(
    width,
    products,
    anchorSide,
    enableTouchPointsOnBothSides
  ), [products, defaultZoom, mobileZoomOffset]);

  const handleCollectionSelection = (newDesignStyleChoiceId) => {

    setCollectionDrawerOpen(false);
    if (pendingSpacerModal) {
      setDrawerOpen(false);
      setSpacerModalOpen(true);
      setPendingSpacerModal(false);
    } else {
      setDrawerOpen(true);
    }
  };

  // Callback for when CabinetDrawer data is ready
  const handleCabinetDrawerDataReady = () => {
    // Data is ready, ensure drawer stays open for CabinetDrawer
    setDrawerOpen(true);
  };

  // Monitor and fix aria-hidden issue
  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement && rootElement.getAttribute('aria-hidden') === 'true') {
      rootElement.removeAttribute('aria-hidden');
    }
  }, [drawerOpen, hasSelectedCollection]);

  const getCabinetTouchPoint = () => {
    if (!selectedCabinet) {
      return selectedTouchPoint;
    }
    const itemId = selectedCabinet?.Sku;
    if (itemId === -1) {
      return { type: ['Base'] };
    }
    if (itemId === -2) {
      return { type: ['Wall'] };
    }
    const touchPoint = {
      type: [selectProductsModalType(cabinetVisualizer, itemId, designId)]
    };
    return touchPoint;
  };

  /**
  * Checks if editing or adding a cabinet is valid by ensuring the new cabinet's
  * position does not collide with other cabinets of the same type (base or wall).
  *
  * @param {Object} cabinetInfo - The cabinet information to validate.
  * @param {string} cabinetInfo.productType - The type of the cabinet (e.g., 'Base', 'Wall').
  * @param {number} cabinetInfo.height - The height of the cabinet.
  * @param {number} cabinetInfo.width - The width of the cabinet.
  * @returns {boolean} True if the cabinet can be added or edited without collision; otherwise, false.
  */
  const isEditCabinetValid = (cabinetInfo) => {
    if (products === EMPTY_ARRAY) {
      return true;
    }
    const productType = cabinetInfo.productType;
    const isBase = isBaseProduct(productType);
    const standardWallCabinetHeight = WALL_SEGMENT_HEIGHT;
    if (!isBase && cabinetInfo.height >= standardWallCabinetHeight) {
      return true;
    }

    const { defaultMinColumn, defaultMaxColumn } = getDefaultMinMaxColumns(anchorSide);
    const isAddingCabinet = !selectedCabinet;
    const otherProducts = isAddingCabinet
      ? products.filter((cab) => cab.Sku !== CABINET_SKU.VOID_SPACE)
      : products.filter((cab) => cab.id !== selectedCabinet?.id && cab.Sku !== CABINET_SKU.VOID_SPACE);

    const baseProducts = otherProducts.filter((product) => isBaseProduct(product.productType));
    const wallProducts = otherProducts.filter((product) => !isBaseProduct(product.productType));

    let minColumn;
    if (isBase) {
      minColumn = baseProducts.length > 0
        ? Math.min(...baseProducts.map((cab) => cab.Origin.Column)) : defaultMinColumn;
    } else {
      minColumn = wallProducts.length > 0
        ? Math.min(...wallProducts.map((cab) => cab.Origin.Column)) : defaultMinColumn;
    }

    let maxColumn;
    if (isBase) {
      maxColumn = baseProducts.length > 0
        ? Math.max(...baseProducts.map((cab) => cab.Origin.Column + cab.Origin.Width)) : defaultMaxColumn;
    } else {
      maxColumn = wallProducts.length > 0
        ? Math.max(...wallProducts.map((cab) => cab.Origin.Column + cab.Origin.Width)) : defaultMaxColumn;
    }

    const maxHeight = DEFAULT_VIEWPORT_WALLSIZE.Rows;

    const newRow = !isBase
      ? (cabinetInfo.height > standardWallCabinetHeight ? standardWallCabinetHeight - cabinetInfo.height : 0)
      : maxHeight - cabinetInfo.height;

    let offsetOnLeft;
    if (isBase) {
      offsetOnLeft = _isEmpty(baseProducts) ? 0 : cabinetInfo.width;
    } else {
      offsetOnLeft = _isEmpty(wallProducts) ? 0 : cabinetInfo.width;
    }

    let offsetOnRight;
    if (!isBase) {
      offsetOnRight = _isEmpty(wallProducts) ? cabinetInfo.width : 0;
    } else {
      offsetOnRight = _isEmpty(baseProducts) ? cabinetInfo.width : 0;
    }

    const defaultColumn = cabinetInfo.direction === DIRECTION.RIGHT
      ? maxColumn - offsetOnRight : minColumn - offsetOnLeft;
    const widthDelta = cabinetInfo.direction === DIRECTION.RIGHT
      ? selectedCabinet?.Origin.Width - cabinetInfo.width : 0;

    const newColumn = isAddingCabinet
      ? defaultColumn
      : selectedCabinet?.Origin.Column + widthDelta;

    const newOrigin = {
      Row: newRow,
      Column: newColumn,
      Height: cabinetInfo.height,
      Width: cabinetInfo.width
    };

    const isCollidedWithOthers = isCollidedWithOtherProducts(productType, newOrigin, otherProducts, 'vertical');
    return !isCollidedWithOthers;
  };
  /**
   * Updates the selected cabinet's properties and position in the design.
   * Adjusts the cabinet's origin, updates the products list, and triggers analytics.
   *
   * @param {Object} cabinetInfo - The new cabinet information.
   * @param {string} cabinetInfo.productType - The type of the cabinet (e.g., 'Base', 'Wall').
   * @param {number} cabinetInfo.height - The new height of the cabinet.
   * @param {number} cabinetInfo.width - The new width of the cabinet.
   * @param {string|number} cabinetInfo.itemId - The new item ID for the cabinet.
   */
  const editCabinet = (cabinetInfo) => {
    const otherCabinets = products.filter((cab) => cab.id !== selectedCabinet?.id);
    const origin = selectedCabinet?.Origin;
    const newOrigin = {
      Row: origin.Row,
      Column: origin.Column,
      Height: cabinetInfo.height,
      Width: cabinetInfo.width
    };
    const newCabinetInfo = {
      ...cabinetInfo,
      id: selectedCabinet?.id,
    };
    const adjustedOrigin = getAdjustedCabinetOriginForEdit({
      productType: cabinetInfo.productType,
      origin: newOrigin,
      cabinet: selectedCabinet,
      products: otherCabinets,
      direction: cabinetInfo.direction,
      cabinetInfo
    });

    const itemId = cabinetInfo.itemId;
    const parentId = ProductParentIds ? ProductParentIds[`${itemId}`] : '';
    const maxHeight = DEFAULT_VIEWPORT_WALLSIZE.Rows;
    let updatedProducts = editProduct({
      cabinetVisualizer,
      designId,
      itemId,
      parentId,
      origin: adjustedOrigin,
      cabinetInfo: newCabinetInfo,
      maxHeight,
      cabinets: products,
      enableTouchPointsOnBothSides
    });
    selectedCabinet.Sku = itemId; // Update selectedCabinet Sku to new itemId

    updateDesignProducts(
      host,
      designId,
      updatedProducts,
      newCabinetInfo ?? { parentId, itemId, id: selectedCabinet?.id }, metadataBaseUrl,
    );
    onEditCabinetAnalytics(itemId, cabinetInfo);
    setSelectedCabinet(null);
  };

  const openDrawer = (event) => {
    setEditMode(true);
    const itemId = selectedCabinet?.Sku;
    const parentId = ProductParentIds ? ProductParentIds[`${itemId}`] : '';
    event.preventDefault();
    event.stopPropagation();
    if (itemId > 0) {
      updateCabinetVisualizer({
        skuDrawer: {
          selectedParentId: parentId,
          selectedItemId: itemId,
        }
      });
      setDrawerOpen(!drawerOpen);
    } else {
      setSpacerModalOpen(true);
    }
  };

  const closeDrawer = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (drawerOpen) {
      setDrawerOpen(false);
    }
    if (spacerModalOpen) {
      setSpacerModalOpen(false);
    }
    if (editMode) {
      setEditMode(false);
    }
  };

  const handleCornerCabinetChange = useCallback((itemId, corner, type) => {
    if (corner && itemId && !cornerCabinetItemIds.includes(itemId)) {
      let updatedCornerCabinetItemIds = [...cornerCabinetItemIds];
      updatedCornerCabinetItemIds.push(itemId);
      setCornerCabinetItemIds(updatedCornerCabinetItemIds);
    }
  }, []);

  const isCornerCabinet = () => {
    if (!selectedCabinet) {
      return false;
    }
    return selectedCabinet?.cornerCabinet || (cornerCabinetItemIds.length > 0
      && cornerCabinetItemIds.includes(selectedCabinet?.Sku));
  };

  const getEditSpacerConfig = () => {
    if (!selectedCabinet) {
      return EMPTY_OBJECT;
    }
    return {
      name: selectedCabinet.Name,
      width: selectedCabinet.Origin.Width,
      height: selectedCabinet.Origin.Height
    };
  };
  const isEmptyWall = !products || products.length === 0;
  // Return an object with everything needed for rendering
  return {
    products,
    viewportInfo,

    disableTouchPoints,
    showDimensions,
    wallName,
    wallSize,
    totalWidth,

    host,
    metadataBaseUrl,
    anchorSide,
    isMobile,
    isEmptyWall,

    cabinetVisualizer,
    addCabinetToDesign,

    editWallModalProps,
    handleEditWallModalOpen,
    handleEditWallModalClose,

    hasValidDesign,
    handleZoomIn,
    handleZoomOut,
    handleToggleSegments,

    dndEnabled,
    segmentsEnabled,
    segments,
    Rows,
    Columns,
    toggleDrawer,
    toggleSpacerModal,
    toggleFillerDrawer,
    handleRenderCallback,
    setViewportInfo,
    wallContainerRef,
    handleWallContainerRefChange,
    setFloorRect,
    updateDesignProducts,
    selectedCabinet,
    isCornerCabinet,

    buttonPosition,
    restartButtonHeight,
    restartButtonWidth,
    setRestartModalOpen,
    iconPosition,
    iconButtonHeight,
    iconButtonWidth,
    openDrawer,
    openConfirmation,
    restartMessage,
    restartHeader,
    handleCloseRestartModal,
    restartModalOpen,
    handleConfirmRestart,
    switchButtonPosition,

    handleZoom,
    handleDimensionsToggle,
    totalDepth,
    totalHeight,

    toggleZoomModal,
    drawerOpen,
    closeDrawer,
    getCabinetTouchPoint,
    handleCabinetDrawerDataReady,
    editMode,
    editCabinet,
    isEditCabinetValid,
    handleCornerCabinetChange,

    collectionDrawerOpen,
    setCollectionDrawerOpen,
    handleCollectionSelection,
    spacerModalOpen,
    setSpacerModalOpen,
    pendingSpacerModal,
    getEditSpacerConfig,

    handleClose,
    removeCabinet,
    getProductType,

    confirmation,
    setConfirmation,

    updateCabinetVisualizerStore
  };
};
